import { useEffect, useMemo, useRef, useState } from "react"
import { IFrameWidget, IFrameWidgetHandle } from "../../../shared/iframes/iframe"
import { BaseWidgetProps, SparqlWidgetProps } from "../../../helpers/widget"
import { RemoteProxy } from "penpal/dist/penpal"
import { GeneralWidgetConfig } from "../../WidgetConfiguration"
import { configOptions, IFCViewerV4ConfigOptions, parseAndSanitizeUrls, RawIFCViewerV4ConfigOptions } from "./config"
import { useAppCtx } from "../../../hooks/use-app-ctx"
import { useSparqlManager } from "../../../helpers/SparqlManager"
import { useAppLoginStore } from "../../../api/app-login"
import { ProgressDisplay, useProgress } from "../../../shared/progress"
import { useSyncModelUrls } from "./use-sync-model-urls"
import { useLatest } from "../../../hooks/use-latest"
import { IFC_PROGRESS_STAGES, IfcProgressStage, IFrameApi, ParentApi } from "./shared/iframe-api"
import { useSetTimestamp } from "../../../helpers/PublishUtils"
import { useIfcConversions } from "./use-ifc-conversions"
import { useWidgetConfig } from "../../../hooks/use-widget-config"
import { parseJsonFields } from "../../../helpers/parse-json-fields"
import { ifcToLdConfigSchema } from "./shared/ifc-to-ld-config"
import { ambientOcclusionConfigSchema } from "./shared/ambient-occlusion-config"
import { IfcToFragConverter } from "./conversion/ifc-to-frag-converter"
import { FragToLdConverter } from "./conversion/frag-to-ld-converter"
import { assertNever } from "../../../utils/assert-never"
import { TriangleAlert } from "lucide-react"
import { z } from "zod"
import { useSyncViewerConfig } from "./use-sync-viewer-config"
import { useSyncSelection } from "./use-sync-selection"
import { useStateWithRef } from "../../../hooks/use-state-with-ref"
import { useMemoizedFn } from "../../../hooks/use-memoized-fn"
import { useUpdateEffect } from "../../../hooks/use-update-effect"
import { useGhostedModels } from "./use-ghosted-models"
import { toast } from "react-toastify"

const IFC_PROGRESS_STAGE_LABELS = {
  "download": "Downloading",
  "load-ifc": "Processing IFC",
  "load-frag": "Processing fragment",
  "convert-ifc-to-frag": "Converting IFC to fragment",
  "upload-frag": "Uploading fragment",
  "stream-ifc-to-ld": "Streaming IFC to LD",
} as const satisfies Record<IfcProgressStage, string>

// This is roughly based on feelings/expectations currently
const IFC_PROGRESS_STAGE_DURATION_BIAS = {
  "download": 4,
  "load-ifc": 2,
  "load-frag": 1,
  "convert-ifc-to-frag": 2,
  "upload-frag": 4,
  "stream-ifc-to-ld": 4,
} as const satisfies Record<IfcProgressStage, number>

export const iframeConnectionsByWidgetId: Record<string, RemoteProxy<IFrameApi> | undefined> = {}

function IFCViewerV4(props: GeneralWidgetConfig & BaseWidgetProps & SparqlWidgetProps & RawIFCViewerV4ConfigOptions) {
  const appCtx = useAppCtx()
  const [ iframeLoadedAt, setIframeLoadedAt ] = useState(performance.now())

  const config = useWidgetConfig(configOptions, props, config => {
    return parseJsonFields<IFCViewerV4ConfigOptions>(config, {
      ifcToLdConfig: ifcToLdConfigSchema,
      ambientOcclusionConfig: ambientOcclusionConfigSchema,
      predefinedColors: z.record(z.string(), z.string()),
    })
  })

  const [ iframeApi, setIframeApi, iframeApiRef ] = useStateWithRef<RemoteProxy<IFrameApi> | undefined>(undefined)
  const iframeWidgetRef = useRef<IFrameWidgetHandle | null>(null)

  const syncSelection = useSyncSelection({
    widgetId: props.widgetId,
    selectionTopic: config.selectionTopic,
    selectionSubscription: config.selectionSubscription,
    multipleselectparametertype: config.multipleselectparametertype,
    namespace: config.ifcToLdConfig?.namespaces?.schema,
    zoomOnSelectionChange: config.zoomOnSelectionChange,
    setIfcSelection: (guids, zoom) => iframeApiRef.current?.setSelection(guids, zoom),
  })

  const applyLayout = useMemoizedFn(async () => {
    if (!iframeApiRef.current) return
    await iframeApiRef.current.setLayoutConfig({
      viewerOnly: config.viewerOnly ?? false,
      contentGridTemplate: config.viewerOnly ? undefined : config.contentGridTemplate
    })
  })

  const [ progress, setProgress ] = useProgress(IFC_PROGRESS_STAGES)
  const latestSetProgress = useLatest(setProgress)
  const parentApi = useMemo<ParentApi>(() => ({
    onConnected: async () => {
      setIframeLoadedAt(performance.now())
      await applyLayout()
    },
    setProgress: (input) => latestSetProgress.current(input),
    setSelection: syncSelection.setAppvarSelection
  }), [ latestSetProgress, syncSelection.setAppvarSelection, applyLayout ])

  const onIframeInit = async (api: RemoteProxy<IFrameApi>) => {
    setIframeApi(api)
    await applyLayout()
  }

  useUpdateEffect(function refreshIframeWhenNecessaryForImmediateUserFeedback() {
    if (!iframeWidgetRef.current) return
    setIframeApi(undefined)
    iframeWidgetRef.current.refresh()
  }, [ config.viewerOnly, config.contentGridTemplate ])

  const ifcUrls = parseAndSanitizeUrls(config.ifcUrls, config.multipleselectparametertype)
  const fragUrls = parseAndSanitizeUrls(config.fragUrls, config.multipleselectparametertype)
  const ghostingSparqlManager = useSparqlManager(appCtx.urlSparql, `${props.widgetId}-ghosting-queries`)
  const ghostedModelsResult = useGhostedModels({
    sparqlManager: ghostingSparqlManager,
    urls: [ ...ifcUrls, ...fragUrls ]
  })
  useEffect(function logErrorIfFetchingGhostedFailed() {
    if (!ghostedModelsResult.isError) return
    console.error("Failed to fetch IFC presentation status (ghosting)", ghostedModelsResult.error)
    toast.error(`Failed to fetch IFC presentation status (ghosting): ${ghostedModelsResult.error.message}`)
  }, [ ghostedModelsResult.isError, ghostedModelsResult.error ])

  useSyncViewerConfig({
    widgetConfig: config,
    iframeApiRef,
    iframeLoadedAt,
    sparqlResults: props.data,
    ghostedModelUrls: ghostedModelsResult.data
  })

  useSyncModelUrls({
    type: "ifc",
    iframeLoadedAt,
    iframeApiRef,
    urls: config.ifcUrls,
    multipleselectparametertype: config.multipleselectparametertype,
    onModelsLoaded: async function loadNewIfcToFragConversionCandidates() {
      await IfcToFragConverter.refetch()
    }
  })

  useSyncModelUrls({
    type: "frag",
    iframeLoadedAt,
    iframeApiRef,
    urls: config.fragUrls,
    multipleselectparametertype: config.multipleselectparametertype,
    onModelsLoaded: async function loadNewFragToLDConversionCandidates() {
      await FragToLdConverter.refetch()
    }
  })

  const conversionSparqlManager = useSparqlManager(appCtx.urlSparql, `${props.widgetId}-conversion-queries`)
  const fullAccess = useAppLoginStore(s => s.status === "AUTHORIZED" && s.user.fullAccess)

  const setTimestamp = useSetTimestamp(config.timestampParameters)
  const conversionsStatus = useIfcConversions({
    autoIfcToFragEnabled: Boolean(config.autoConvertToFrag) && fullAccess,
    autoIfcToLdEnabled: Boolean(config.autoConvertToLd) && fullAccess,
    appCtx,
    iframeLoadedAt,
    iframeApiRef,
    ifcUrls,
    fragUrls,
    sparqlManager: conversionSparqlManager,
    widgetConfig: config,
    progressCallback: p => setProgress({ type: "update", ...p }),
    setUpdateTimestamp: setTimestamp
  })

  useEffect(function publishGlobalIframeConnectionsByWidget() {
    const widgetId = props.widgetId
    iframeConnectionsByWidgetId[widgetId] = iframeApi
    return () => {
      delete iframeConnectionsByWidgetId[widgetId]
    }
  }, [ iframeApi, props.widgetId ])

  const conversionsCannotBeFacilitatedMessage = useMemo(() => {
    switch (conversionsStatus) {
      case "ok":
      case "disabled": /* fallthough */
        return null
      case "wistor-backend-required":
        return "IFC Conversions are only supported when connected to a Wistor backend"
      case "no-iframe-api":
        // Currently no message here, as it briefly is in no-iframe-api state while connection is getting established
        return null
      default:
        assertNever(conversionsStatus)
    }
  }, [ conversionsStatus ])

  return (
    <div className="flex flex-col h-full">
      {conversionsCannotBeFacilitatedMessage && (
        <div className="absolute z-10 top-0 left-1/2 -translate-x-1/2
                        flex gap-1 m-2 py-1 px-2 items-center rounded
                        text-xs bg-yellow-200 text-orange-800">
          <TriangleAlert size={16} />
          {conversionsCannotBeFacilitatedMessage}
        </div>
      )}
      <ProgressDisplay
        theme="dark"
        progress={progress}
        stageLabels={IFC_PROGRESS_STAGE_LABELS}
        stageDurationBias={IFC_PROGRESS_STAGE_DURATION_BIAS}
        className="absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 min-w-[min(80vw,300px)]"
        onHidden={() => setProgress({ type: "reset" })}
      />
      <IFrameWidget
        ref={iframeWidgetRef}
        iframeProps={{
          className: "flex-grow"
        }}
        widget={props.widget}
        title="IFC Viewer"
        url={{
          prod: `/${window.bbFolderName}/static/iframes/ifc-viewer-v4/index.html`,
          dev: "http://localhost:5173"
        }}
        parentApi={parentApi}
        onConnectionEstablished={onIframeInit}
      />
    </div>
  )
}

export default IFCViewerV4
